import type { CandidateLocation, EvidenceItem, StoreMetric } from "./types";

export const douglasCourtCandidate: CandidateLocation = {
  id: "douglas-court-qa",
  is_sample: false,
  name: "Douglas Court Shopping Centre",
  county: "Cork",
  stage: "unit",
  status: "research",
  recommendation: "research",
  site_address: "Douglas Court Shopping Centre, Douglas Relief Road, Ballybrack, Douglas, Cork",
  eircode: "T12 HK5D",
  latitude: 51.8783595,
  longitude: -8.4310145,
  location_source: "Douglas Court official website; OpenStreetMap centre pin",
  pin_adjusted: false,
  location_confirmed_at: "2026-08-17T17:00:00Z",
  location_confirmed_by: null,
  town_score: null,
  unit_score: null,
  confidence: 0,
  forecast_monthly_p10: null,
  forecast_monthly_p50: null,
  forecast_monthly_p90: null,
  forecast_payback_years: null,
  forecast_own_cash: null,
  research_state: "draft",
  research_requested_at: null,
  objective: "Assess Douglas Court Shopping Centre as a potential NAMO location. The centre is confirmed; the specific retail unit remains to be identified and assessed.",
  target_opening_date: null,
  decision_deadline: null,
  proposed_format: "Shopping-centre restaurant — specific unit TBC",
  competitor_context: null,
  annual_rent: null,
  floor_area_sqft: null,
  fitout_estimate: null,
  frontage_notes: null,
  parking_notes: null,
  context_notes: "Release 2 quality-assurance case. The confirmed point identifies Douglas Court Shopping Centre, not a specific available unit. No score or recommendation should be inferred until sourced research and unit economics are recorded.",
  context_links: "https://www.douglascourt.ie/",
  education_context: null,
  workforce_context: null,
  employment_context: null,
  delivery_context: null,
  comparable_store_keys: [],
  trading_days_per_week: null,
  trading_hours_per_week: null,
  counter_orders_per_day: null,
  average_counter_check: null,
  delivery_revenue_share: null,
  downside_adjustment_pct: 15,
  upside_adjustment_pct: 15,
  annual_rates: null,
  annual_service_charge: null,
  landlord_contribution: null,
  rent_free_months: null,
  seating_capacity: null,
  peak_orders_per_hour: null,
  centre_monthly_footfall: null,
  footfall_source: null,
  forecast_notes: null,
  forecast_calculated_at: null,
  updated_at: "2026-08-17T17:00:00Z",
};

export const douglasCourtEvidence: EvidenceItem[] = [];

export const navanCandidate: CandidateLocation = {
  id: "navan-pilot",
  is_sample: true,
  name: "Navan",
  county: "Meath",
  stage: "town",
  status: "assessment",
  recommendation: "research",
  site_address: "Navan town-centre assessment anchor (not a selected unit)",
  eircode: null,
  latitude: 53.6528,
  longitude: -6.6814,
  location_source: "Town-centre research anchor",
  pin_adjusted: false,
  location_confirmed_at: null,
  location_confirmed_by: null,
  town_score: null,
  unit_score: null,
  confidence: 70,
  forecast_monthly_p10: 68000,
  forecast_monthly_p50: 88000,
  forecast_monthly_p90: 113000,
  forecast_payback_years: 2.4,
  forecast_own_cash: 165500,
  research_state: "review",
  research_requested_at: "2026-08-11T09:00:00Z",
  objective: "Assess Navan as the next regional NAMO location before progressing to unit search.",
  target_opening_date: "2027-03-01",
  decision_deadline: "2026-09-30",
  proposed_format: "Town-centre restaurant with delivery",
  competitor_context: "Named local competitor audit still required: identify direct Mexican/QSR offers, delivery-only operators and established fast-casual brands before approval.",
  annual_rent: null,
  floor_area_sqft: null,
  fitout_estimate: null,
  frontage_notes: null,
  parking_notes: null,
  context_notes: "Audited town-stage example using the Navan town-centre coordinates as an assessment anchor, not a selected unit. Prioritise sustainable year-round demand and avoid treating students or commuters as guaranteed footfall.",
  context_links: "https://www.cso.ie/en/releasesandpublications/ep/p-cpp1/censusofpopulation2022profile1-populationdistributionandmovements/populationdistribution/\nhttps://www.gov.ie/en/department-of-education/collections/post-primary-schools-enrolment-figures/\nhttps://namo-performance.methodai.ie/",
  education_context: "Five Navan post-primary schools enrol 4,349 students. Four schools representing 3,476 students are within 1.11 km straight-line of the town-centre assessment anchor. Navan has no HEA-funded main higher-education campus; further-education activity must not be treated as equivalent daily campus demand.",
  workforce_context: "Census 2022 records 2,953 Navan residents aged 18–24, indicating a meaningful potential Crew pool. Applicant conversion, late-shift transport and exam/holiday rota resilience still require NAMO HR evidence.",
  employment_context: "Navan is a regional employment centre, but exact business-park, employer and route-time evidence must be recalculated from a selected premises.",
  delivery_context: "Existing NAMO delivery performance provides an analogue only. Exact road-network reach and anonymised Just Eat order-origin evidence remain required.",
  comparable_store_keys: ["clonmel", "portlaoise"],
  trading_days_per_week: null,
  trading_hours_per_week: null,
  counter_orders_per_day: null,
  average_counter_check: null,
  delivery_revenue_share: null,
  downside_adjustment_pct: 15,
  upside_adjustment_pct: 15,
  annual_rates: null,
  annual_service_charge: null,
  landlord_contribution: null,
  rent_free_months: null,
  seating_capacity: null,
  peak_orders_per_hour: null,
  centre_monthly_footfall: null,
  footfall_source: null,
  forecast_notes: null,
  forecast_calculated_at: null,
  updated_at: "2026-08-15T13:24:00Z",
};

export const navanEvidence: EvidenceItem[] = [
  {
    id: "navan-population",
    candidate_id: navanCandidate.id,
    dimension: "demand",
    metric_key: "general",
    label: "Town population",
    value_number: 33886,
    value_text: null,
    unit: "people",
    normalized_score: 86,
    weight: 25,
    confidence: 95,
    source_name: "CSO Census 2022",
    source_tier: "official",
    source_url: "https://www.cso.ie/en/releasesandpublications/ep/p-cpp1/censusofpopulation2022profile1-populationdistributionandmovements/populationdistribution/",
    source_date: "2022-04-03",
    note: "Navan is one of Ireland's five largest towns. The 2022 built-up-area boundary changed, so the 2016 comparison is directional rather than like-for-like.",
    is_required: true,
  },
  {
    id: "navan-commuting",
    candidate_id: navanCandidate.id,
    dimension: "demand",
    metric_key: "general",
    label: "Dublin commuter share",
    value_number: 21.96,
    value_text: "3,311 of 15,080 resident workers",
    unit: "%",
    normalized_score: 78,
    weight: 0,
    confidence: 95,
    source_name: "CSO Employment & Commuting 2022",
    source_tier: "official",
    source_url: "https://www.cso.ie/en/releasesandpublications/ep/p-cpp7/censusofpopulation2022profile7-employmentoccupationsandcommuting/commutingtowork/",
    source_date: "2022-04-03",
    note: "A strong commuter base supports evening, delivery and weekend demand but may reduce weekday daytime presence.",
    is_required: false,
  },
  {
    id: "navan-analogue",
    candidate_id: navanCandidate.id,
    dimension: "analogue",
    metric_key: "general",
    label: "NAMO comparable-store signal",
    value_number: 3,
    value_text: "Carlow, Kilkenny and Portlaoise",
    unit: "analogues",
    normalized_score: 78,
    weight: 25,
    confidence: 80,
    source_name: "NAMO weekly sales tracker",
    source_tier: "first_party",
    source_url: "https://namo-performance.methodai.ie/",
    source_date: "2026-08-03",
    note: "Provisional blend using mature rolling 13-week performance. NAMO Plus is excluded; Athlone remains immature.",
    is_required: true,
  },
  {
    id: "navan-competition",
    candidate_id: navanCandidate.id,
    dimension: "competition",
    metric_key: "general",
    label: "Mexican/QSR whitespace",
    value_number: null,
    value_text: "Paid competitor audit required",
    unit: null,
    normalized_score: 55,
    weight: 20,
    confidence: 35,
    source_name: "Research requirement",
    source_tier: "estimate",
    source_url: null,
    source_date: null,
    note: "Validate direct competitors, delivery coverage, openings, closures and trading longevity before approval.",
    is_required: true,
  },
  {
    id: "navan-access",
    candidate_id: navanCandidate.id,
    dimension: "access",
    metric_key: "general",
    label: "Catchment and accessibility",
    value_number: null,
    value_text: "County town and regional retail centre",
    unit: null,
    normalized_score: 74,
    weight: 15,
    confidence: 80,
    source_name: "Meath County Development Plan 2021-2027",
    source_tier: "official",
    source_url: "https://consult.meath.ie/en/system/files/materials/8703/Navan___0.pdf",
    source_date: "2025-01-01",
    note: "Navan is an administrative, retail, health and education centre with a town-centre public realm programme.",
    is_required: true,
  },
  {
    id: "navan-risk",
    candidate_id: navanCandidate.id,
    dimension: "risk",
    metric_key: "general",
    label: "Development trajectory",
    value_number: null,
    value_text: "Mixed-use growth pipeline",
    unit: null,
    normalized_score: 76,
    weight: 15,
    confidence: 75,
    source_name: "Meath County Council master plans",
    source_tier: "official",
    source_url: "https://www.meath.ie/council/council-services/planning-and-building/development-plans/master-plans",
    source_date: "2026-08-01",
    note: "Town-centre expansion and mixed-use master plans are positive, but delivery dates and unit-level footfall still need verification.",
    is_required: true,
  },
  {
    id: "navan-higher-education",
    candidate_id: navanCandidate.id,
    dimension: "education",
    metric_key: "higher_education",
    label: "Third-level student catchment",
    value_number: null,
    value_text: "No HEA-funded higher-education campus in Navan",
    unit: "students",
    normalized_score: 22,
    weight: 35,
    confidence: 90,
    source_name: "Higher Education Authority",
    source_tier: "official",
    source_url: "https://hea.ie/statistics/data-for-download-and-visualisations/key-facts-figures-report/",
    source_date: "2025-11-17",
    note: "LMETB further and adult education provision exists locally, but it is not equivalent to a main higher-education campus or a verified daily student-footfall figure.",
    is_required: true,
  },
  {
    id: "navan-secondary-schools",
    candidate_id: navanCandidate.id,
    dimension: "education",
    metric_key: "secondary_schools",
    label: "Secondary-school catchment",
    value_number: 4349,
    value_text: "4,349 students across five Navan post-primary schools",
    unit: "pupils",
    normalized_score: 90,
    weight: 20,
    confidence: 95,
    source_name: "Department of Education and Youth",
    source_tier: "official",
    source_url: "https://www.gov.ie/en/department-of-education/collections/post-primary-schools-enrolment-figures/",
    source_date: "2026-06-10",
    note: "Department records: St Patrick's Classical 938; Beaufort College 917; Loreto Secondary 909; Coláiste na Mí 873; St Joseph's Secondary 712. This is a town catchment, not guaranteed custom.",
    is_required: true,
  },
  {
    id: "navan-campus-access",
    candidate_id: navanCandidate.id,
    dimension: "education",
    metric_key: "campus_access",
    label: "Campus and school access",
    value_number: 3476,
    value_text: "Four of five schools (3,476 students) are within 1.11 km straight-line of the town-centre anchor",
    unit: "students",
    normalized_score: 82,
    weight: 20,
    confidence: 72,
    source_name: "OpenStreetMap desktop catchment audit",
    source_tier: "observed",
    source_url: "https://www.openstreetmap.org/#map=14/53.6528/-6.6814",
    source_date: "2026-08-15",
    note: "Straight-line distances from the assessment anchor: Loreto 0.34 km; St Joseph's 0.59 km; Beaufort 1.07 km; St Patrick's 1.11 km; Coláiste na Mí 2.36 km. A selected unit still needs a walked route, gate and lunchtime footfall check.",
    is_required: true,
  },
  {
    id: "navan-education-seasonality",
    candidate_id: navanCandidate.id,
    dimension: "education",
    metric_key: "education_seasonality",
    label: "Academic-holiday demand resilience",
    value_number: 101.3,
    value_text: "Education-led analogues retained a median 101.3% of late-term weekly sales during summer 2026",
    unit: "% retained",
    normalized_score: 78,
    weight: 25,
    confidence: 72,
    source_name: "NAMO weekly sales tracker",
    source_tier: "first_party",
    source_url: "https://namo-performance.methodai.ie/",
    source_date: "2026-08-09",
    note: "Carlow, Kilkenny and Waterford were compared across weeks 18–25 and 27–31. This short single-year analogue test is encouraging, not causal; a full-year term-versus-holiday series is still required for model calibration.",
    is_required: true,
  },
  {
    id: "navan-crew-supply",
    candidate_id: navanCandidate.id,
    dimension: "workforce",
    metric_key: "crew_supply",
    label: "Student Crew recruitment pool",
    value_number: 2953,
    value_text: "2,953 Navan residents aged 18–24 form a meaningful potential Crew catchment",
    unit: "people",
    normalized_score: 76,
    weight: 50,
    confidence: 80,
    source_name: "CSO Census 2022 single-year age profile",
    source_tier: "official",
    source_url: "https://data.gov.ie/dataset/sap2022t1t1town22-population",
    source_date: "2022-04-03",
    note: "The demographic pool is verified; willingness to work, competing employers, transport and applications per vacancy are not. Treat this as recruitment potential rather than expected hires.",
    is_required: true,
  },
  {
    id: "navan-crew-resilience",
    candidate_id: navanCandidate.id,
    dimension: "workforce",
    metric_key: "crew_resilience",
    label: "Crew availability through exams and holidays",
    value_number: null,
    value_text: "Peak-period coverage and non-student staffing evidence required",
    unit: "% shifts covered",
    normalized_score: null,
    weight: 50,
    confidence: 0,
    source_name: "NAMO HR and rota history required",
    source_tier: "first_party",
    source_url: null,
    source_date: null,
    note: "This is the remaining blocker. Supply applications per vacancy, time to fill, staff age mix, exam and holiday availability, and unfilled-shift rates from comparable stores before marking Crew resilience verified.",
    is_required: true,
  },
  {
    id: "navan-workplace-concentration", candidate_id: navanCandidate.id, dimension: "daytime_trade", metric_key: "workplace_concentration", label: "Workplace and business concentration", value_number: null, value_text: "Exact-premises business catchment still required", unit: "businesses", normalized_score: 62, weight: 30, confidence: 35, source_name: "CSO POWSCAR and GeoDirectory research requirement", source_tier: "estimate", source_url: "https://www.cso.ie/en/census/census2022/powscar/", source_date: "2022-04-03", note: "Town-level workplace context cannot substitute for a routed catchment from a selected premises.", is_required: true,
  },
  {
    id: "navan-major-employers", candidate_id: navanCandidate.id, dimension: "daytime_trade", metric_key: "major_employers", label: "Major audience generators", value_number: null, value_text: "Named employer and on-site workforce verification required", unit: "workers", normalized_score: 58, weight: 25, confidence: 25, source_name: "IDA Ireland and employer verification required", source_tier: "estimate", source_url: "https://www.idaireland.com/map", source_date: "2026-08-15", note: "Do not treat company-wide headcount or an unverified directory estimate as people present near the proposed unit.", is_required: true,
  },
  {
    id: "navan-lunch-access", candidate_id: navanCandidate.id, dimension: "daytime_trade", metric_key: "lunch_access", label: "Daypart accessibility", value_number: null, value_text: "Walking routes from nearby audiences require a selected premises", unit: "minutes", normalized_score: 55, weight: 20, confidence: 20, source_name: "Route and site audit required", source_tier: "estimate", source_url: null, source_date: "2026-08-15", note: "Record routed walking time, crossings, parking and access across breakfast, lunch and evening dayparts; do not use straight-line distance.", is_required: true, route_mode: "walk",
  },
  {
    id: "navan-delivery-opportunity", candidate_id: navanCandidate.id, dimension: "daytime_trade", metric_key: "delivery_opportunity", label: "Road-network delivery opportunity", value_number: null, value_text: "10, 15 and 20-minute driving bands not yet modelled", unit: "orders", normalized_score: 60, weight: 15, confidence: 25, source_name: "NAMO and Just Eat aggregate data required", source_tier: "estimate", source_url: "https://namo-performance.methodai.ie/", source_date: "2026-08-03", note: "This is not confirmed Just Eat coverage. Request anonymised order origins aggregated to safe catchments.", is_required: true, route_mode: "drive",
  },
  {
    id: "navan-employment-resilience", candidate_id: navanCandidate.id, dimension: "daytime_trade", metric_key: "employment_resilience", label: "Employment diversity and resilience", value_number: null, value_text: "Employer concentration, hybrid work and shift exposure require verification", unit: "%", normalized_score: 50, weight: 10, confidence: 20, source_name: "Employer and site research required", source_tier: "estimate", source_url: null, source_date: "2026-08-15", note: "Flag if one employer or employment node represents 50% or more of the identified worker opportunity.", is_required: true,
  },
];

export const fallbackStoreMetrics: StoreMetric[] = [
  { storeId: "carlow", name: "Carlow", weeks: 13, monthlyRunRate: 125682, deliveryShare: 19.1, breakevenMultiple: 2.83, volatility: 6.3, latestWeek: "2026-08-03" },
  { storeId: "kilkenny", name: "Kilkenny", weeks: 13, monthlyRunRate: 104862, deliveryShare: 17.4, breakevenMultiple: 2.05, volatility: 7.9, latestWeek: "2026-08-03" },
  { storeId: "portlaoise", name: "Portlaoise", weeks: 13, monthlyRunRate: 79978, deliveryShare: 17.0, breakevenMultiple: 1.19, volatility: 8.0, latestWeek: "2026-08-03" },
  { storeId: "waterford", name: "Waterford", weeks: 13, monthlyRunRate: 73435, deliveryShare: 23.6, breakevenMultiple: 1.14, volatility: 11.2, latestWeek: "2026-08-03" },
  { storeId: "clonmel", name: "Clonmel", weeks: 13, monthlyRunRate: 67967, deliveryShare: 17.5, breakevenMultiple: 1.39, volatility: 5.4, latestWeek: "2026-08-03" },
  { storeId: "athlone", name: "Athlone", weeks: 3, monthlyRunRate: 155008, deliveryShare: 16.8, breakevenMultiple: null, volatility: 10.4, latestWeek: "2026-08-03", isProvisional: true },
];

export const scoreDimensions = [
  { key: "demand", label: "Core commercial demand", weight: 14 },
  { key: "education", label: "Education demand", weight: 8 },
  { key: "analogue", label: "NAMO analogue performance", weight: 23 },
  { key: "competition", label: "Competition & whitespace", weight: 13 },
  { key: "access", label: "Catchment & accessibility", weight: 12 },
  { key: "risk", label: "Development & local risk", weight: 8 },
  { key: "workforce", label: "Crew availability", weight: 10 },
  { key: "daytime_trade", label: "Audience access & delivery", weight: 12 },
] as const;

export const evidenceTemplates = [
  { key: "general", label: "General evidence", dimension: "demand", weight: 1 },
  { key: "higher_education", label: "Third-level student catchment", dimension: "education", weight: 35 },
  { key: "secondary_schools", label: "Secondary-school catchment", dimension: "education", weight: 20 },
  { key: "campus_access", label: "Campus and school access", dimension: "education", weight: 20 },
  { key: "education_seasonality", label: "Academic-holiday demand resilience", dimension: "education", weight: 25 },
  { key: "crew_supply", label: "Student Crew recruitment pool", dimension: "workforce", weight: 50 },
  { key: "crew_resilience", label: "Crew availability through exams and holidays", dimension: "workforce", weight: 50 },
  { key: "workplace_concentration", label: "Workplace and business concentration", dimension: "daytime_trade", weight: 30 },
  { key: "major_employers", label: "Major audience generators", dimension: "daytime_trade", weight: 25 },
  { key: "lunch_access", label: "Daypart accessibility", dimension: "daytime_trade", weight: 20 },
  { key: "delivery_opportunity", label: "Road-network delivery opportunity", dimension: "daytime_trade", weight: 15 },
  { key: "employment_resilience", label: "Employment diversity and resilience", dimension: "daytime_trade", weight: 10 },
] as const;

export const requiredDecisionMetrics = evidenceTemplates.filter((item) => item.key !== "general");
